/* eslint-disable jsx-a11y/alt-text */
'use client'

import {
  Anchor,
  Image,
  OpeningTitle,
  Paragraph,
  Section,
  TestimonialCard,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import NextImage from 'next/image'
import Link from 'next/link'

import LinkArrowRight from '@/components/atoms/Icons/link-arrow'
import {
  ImageTextModelType,
  LayoutDirectionType,
  LayoutDistributionType,
  NormalizedSection,
  SectionBackground,
} from '@/types'
import { CSSProperties, useMemo } from 'react'

import { useParams } from 'next/navigation'
import { handleOnClickModel } from '@/helpers/models'
import DOMPurify from 'isomorphic-dompurify'
import { dispatchGTMEvent, GTM_EVENT_CATEGORY } from '@/helpers/gtm'

export interface ImageTextProps extends NormalizedSection {
  modelledContent: ImageTextModelType
  props?: {
    bg?: string | 'white' | 'dark' | 'black' | 'grey-050'
    containerBg?: string
    direction: LayoutDirectionType
    distribution: LayoutDistributionType
    // NOTE
    // This prop, when set to `true` from the CMS,
    // will remove the section from the graphQL response,
    // so it'll never make it to the frontend to be read
    // by a component. So it's ultimately useless to handle it
    // and should be ignored in frontend code.
    hideSection: boolean
  }
}

const PARTNER_SLUGS = ['partner', 'partners']

function ImageText({ modelledContent, props }: ImageTextProps) {
  const params = useParams()
  // In case of multiple path segments, we just check the 1st one
  // Also, the site can only go 1 level deep
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug

  const { openingTitle, title, paragraph, cta, image, testimonialCard } =
    modelledContent
  const { fit, imageProps } = image
  const { bg, containerBg, direction, distribution } = props || {
    bg: 'white',
  }

  const openingTitleLabel = openingTitle?.content
  const titleLabel = title?.content
  const paragraphContent = paragraph?.content
  const ctaLabel = cta?.label

  const isPartnerPage = useMemo(() => PARTNER_SLUGS.includes(slug), [slug])

  const _title = useMemo(
    () =>
      !isPartnerPage
        ? ''
        : DOMPurify.sanitize(titleLabel ?? '', {
            USE_PROFILES: { html: false },
          }),
    [titleLabel, isPartnerPage]
  )

  return (
    <Section
      bg={bg as SectionBackground | undefined}
      className={`section-image-text`}
      data-container-bg={!!containerBg}
      data-direction={direction ?? 'row'}
      data-distribution={distribution ?? 'text-image'}
      style={
        {
          '--container-bg': `var(--airc-ui__colors-${containerBg})`,
        } as CSSProperties
      }
    >
      <div className='section-image-text__grid'>
        <div className='section-image-text__grid__col-1'>
          <div className={`section-image-text__title-wrapper`}>
            {openingTitleLabel ? (
              <OpeningTitle
                className={`section-image-text__opening-title`}
                {...openingTitle}
              >
                {openingTitleLabel}
              </OpeningTitle>
            ) : null}

            {titleLabel ? (
              <Title
                className={`section-image-text__title`}
                {...title}
                dangerouslySetInnerHTML={{ __html: titleLabel }}
              />
            ) : null}
          </div>

          {paragraphContent ? (
            <Paragraph
              // We override Paragraph's default alignment by following the Title's
              // alignment; this is needed because the WYSIWYG editor won't pass
              // any style for its default alignment (which is left).
              // If the user sets an alignment other than left in the WYSIWYG editor,
              // the resulting HTML with inline styles will override this alignment.
              alignment={title?.alignment}
              as={'div'}
              className={`section-image-text__paragraph`}
              dangerouslySetInnerHTML={{ __html: paragraphContent }}
              {...paragraph}
            />
          ) : null}

          {testimonialCard ? (
            // @ts-ignore
            <TestimonialCard
              className={'section-image-text__testimonial-card'}
              {...testimonialCard}
              {...(testimonialCard.onClick
                ? { onClick: handleOnClickModel(testimonialCard.onClick) }
                : {})}
            />
          ) : null}

          {cta && ctaLabel ? (
            <Anchor
              // Enable tracking if the page is a partner page
              // (assumes ALL components in the partner page
              // are used to display partnerships).
              {...(isPartnerPage
                ? {
                    onClickCapture() {
                      dispatchGTMEvent(
                        GTM_EVENT_CATEGORY.PARTNER_CLICKTHROUGH,
                        {
                          section: _title,
                        }
                      )
                    },
                  }
                : {})}
              wrapper={'div'}
              data-alignment={cta?.alignment}
              className={`section-image-text__cta link-button-themed`}
              // variant={'themed'}
              as={Link}
              {...cta}
            >
              {ctaLabel}
              <LinkArrowRight className={`section-image-text__cta__icon`} />
            </Anchor>
          ) : null}
        </div>

        <div className='section-image-text__grid__col-2'>
          {imageProps?.src ? (
            <Image
              fit={fit}
              className={`section-image-text__image`}
              as={NextImage}
              imageProps={{
                ...imageProps,
                // If not present (which it will not be,
                // since the CMS is not returning them),
                // it'll take the default value.
                // TODO find out why these props were expected here,
                // @ts-ignore
                width: imageProps.width || 400,
                // @ts-ignore
                height: imageProps.height || 400,
              }}
            />
          ) : null}

          {testimonialCard ? (
            // @ts-ignore
            <TestimonialCard
              className={'section-image-text__testimonial-card'}
              {...testimonialCard}
              {...(testimonialCard.onClick
                ? { onClick: handleOnClickModel(testimonialCard.onClick) }
                : {})}
            />
          ) : null}
        </div>
      </div>
    </Section>
  )
}

export default ImageText
